<template>
  <div>
    <b-card no-body>
      <b-card-header class="d-flex justify-content-between align-items-center">
        <div>
          <b-card-title class="mb-25">État des services</b-card-title>
          <b-card-sub-title>
            <span v-if="lastCheckedAt">Dernière vérification : {{ formattedLastChecked }}</span>
            <span v-else>Vérification en cours…</span>
          </b-card-sub-title>
        </div>
        <div class="d-flex align-items-center">
          <b-badge
            :variant="globalVariant"
            class="mr-2"
            pill
          >
            {{ globalLabel }}
          </b-badge>
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="loading"
            @click="fetchHealth"
          >
            <feather-icon
              icon="RefreshCwIcon"
              :class="{ 'spin': loading }"
            />
            Rafraîchir
          </b-button>
        </div>
      </b-card-header>

      <b-table
        :items="rows"
        :fields="fields"
        :busy="loading && rows.length === 0"
        responsive
        class="mb-0"
      >
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner small class="align-middle" />
            <strong class="ml-1">Chargement…</strong>
          </div>
        </template>

        <template #cell(label)="data">
          <feather-icon
            :icon="data.item.icon"
            class="mr-50"
          />
          <span class="font-weight-bolder">{{ data.item.label }}</span>
        </template>

        <template #cell(status)="data">
          <b-badge :variant="data.item.status === 'up' ? 'light-success' : 'light-danger'" pill>
            {{ data.item.status === 'up' ? 'OK' : 'Hors service' }}
          </b-badge>
        </template>

        <template #cell(latency_ms)="data">
          <span v-if="data.item.latency_ms !== null && data.item.latency_ms !== undefined">
            {{ data.item.latency_ms }} ms
          </span>
          <span v-else>—</span>
        </template>

        <template #cell(message)="data">
          <small class="text-muted">{{ data.item.message || '' }}</small>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BCardHeader, BCardTitle, BCardSubTitle,
  BTable, BBadge, BButton, BSpinner,
} from 'bootstrap-vue'

const SERVICE_META = {
  db: { label: 'Base de données (principale)', icon: 'DatabaseIcon' },
  db_backup: { label: 'Base de données (backup)', icon: 'DatabaseIcon' },
  idex: { label: 'DEKRA / IDEX', icon: 'ClipboardIcon' },
  salesforce: { label: 'Salesforce', icon: 'CloudIcon' },
  spoticar: { label: 'Spoticar', icon: 'TruckIcon' },
}

const REFRESH_INTERVAL_MS = 30000

export default {
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardSubTitle,
    BTable,
    BBadge,
    BButton,
    BSpinner,
  },
  data() {
    return {
      loading: false,
      globalStatus: null,
      lastCheckedAt: null,
      services: {},
      refreshTimer: null,
      fields: [
        { key: 'label', label: 'Service' },
        { key: 'status', label: 'État' },
        { key: 'latency_ms', label: 'Latence' },
        { key: 'message', label: 'Détail' },
      ],
    }
  },
  computed: {
    rows() {
      return Object.keys(SERVICE_META).map(key => {
        const data = this.services[key] || {}
        return {
          key,
          label: SERVICE_META[key].label,
          icon: SERVICE_META[key].icon,
          status: data.status || 'unknown',
          latency_ms: data.latency_ms,
          message: data.message,
        }
      })
    },
    globalVariant() {
      if (this.globalStatus === 'ok') return 'light-success'
      if (this.globalStatus === 'degraded') return 'light-danger'
      return 'light-secondary'
    },
    globalLabel() {
      if (this.globalStatus === 'ok') return 'Tous les services sont opérationnels'
      if (this.globalStatus === 'degraded') return 'Service(s) indisponible(s)'
      return 'Statut inconnu'
    },
    formattedLastChecked() {
      if (!this.lastCheckedAt) return ''
      const d = new Date(this.lastCheckedAt)
      return d.toLocaleTimeString('fr-FR')
    },
  },
  mounted() {
    this.fetchHealth()
    this.refreshTimer = setInterval(this.fetchHealth, REFRESH_INTERVAL_MS)
  },
  beforeDestroy() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }
  },
  methods: {
    fetchHealth() {
      if (this.loading) return
      this.loading = true
      this.$http.get('/api/health')
        .then(response => {
          const payload = response.data || {}
          this.globalStatus = payload.status || null
          this.services = payload.services || {}
          this.lastCheckedAt = payload.checked_at || new Date().toISOString()
        })
        .catch(() => {
          this.globalStatus = 'degraded'
          this.lastCheckedAt = new Date().toISOString()
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>

<style scoped>
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
</style>
